import React, { useEffect, useRef, useState } from 'react';

/**
 * MiloThinking — shared "Milo is working" indicator for long-running actions.
 *
 * Long Milo actions (~20s) previously gave no clear processing feedback, and
 * each screen rolled its own ad-hoc spinner. This is the one shared, purely
 * presentational indicator: a gentle gold/dark orb, a label, optional rotating
 * step text, and — after ~8s — a reassurance line so the user knows it's still
 * working rather than stuck.
 *
 * Props:
 *   label   string            e.g. "Milo is thinking…"
 *   steps   string[]          optional rotating step captions
 *   variant 'inline'|'panel'  layout (default 'inline')
 *   active  bool              when false, renders nothing (default true)
 *
 * Accessibility: role="status" aria-live="polite"; honors
 * prefers-reduced-motion (static orb + label, no animation).
 */

const GOLD = '#C6A75A';
const GOLD_DK = '#8E7A2C';
const DARK = '#1B1812';
const MUTED = '#5C5C66';
const BORDER_GOLD = 'rgba(198,167,90,0.32)';

const REASSURE_AFTER_MS = 8000;
const STEP_ROTATE_MS = 2600;
const REASSURANCE = 'Still working — this can take up to ~20 seconds.';

function usePrefersReducedMotion() {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const apply = () => setReduced(!!mq.matches);
    apply();
    // addEventListener is the modern API; addListener is the legacy fallback.
    if (mq.addEventListener) mq.addEventListener('change', apply);
    else if (mq.addListener) mq.addListener(apply);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', apply);
      else if (mq.removeListener) mq.removeListener(apply);
    };
  }, []);
  return reduced;
}

export default function MiloThinking({
  label = 'Milo is thinking…',
  steps,
  variant = 'inline',
  active = true,
}) {
  const reducedMotion = usePrefersReducedMotion();
  const [stepIdx, setStepIdx] = useState(0);
  const [showReassure, setShowReassure] = useState(false);
  const reassureTimer = useRef(null);
  const stepTimer = useRef(null);

  const hasSteps = Array.isArray(steps) && steps.length > 0;

  // Reassurance after ~8s of continuous activity.
  useEffect(() => {
    if (!active) {
      setShowReassure(false);
      return undefined;
    }
    setShowReassure(false);
    reassureTimer.current = setInterval(() => {
      setShowReassure(true);
      if (reassureTimer.current) {
        clearInterval(reassureTimer.current);
        reassureTimer.current = null;
      }
    }, REASSURE_AFTER_MS);
    return () => {
      if (reassureTimer.current) {
        clearInterval(reassureTimer.current);
        reassureTimer.current = null;
      }
    };
  }, [active]);

  // Rotate step captions while active.
  useEffect(() => {
    if (!active || !hasSteps) {
      setStepIdx(0);
      return undefined;
    }
    stepTimer.current = setInterval(() => {
      setStepIdx((i) => (i + 1) % steps.length);
    }, STEP_ROTATE_MS);
    return () => {
      if (stepTimer.current) {
        clearInterval(stepTimer.current);
        stepTimer.current = null;
      }
    };
  }, [active, hasSteps, steps]);

  if (!active) return null;

  const isPanel = variant === 'panel';
  const animate = !reducedMotion;

  const wrap = isPanel
    ? {
        display: 'flex', alignItems: 'flex-start', gap: 12,
        padding: '14px 16px', borderRadius: 14,
        background: '#FFFFFF', border: `1px solid ${BORDER_GOLD}`,
        boxShadow: '0 2px 12px rgba(198,167,90,0.10)',
        maxWidth: '100%',
      }
    : {
        display: 'inline-flex', alignItems: 'center', gap: 10,
        padding: '4px 0',
      };

  const orbSize = isPanel ? 28 : 18;

  return (
    <div
      role="status"
      aria-live="polite"
      data-testid="milo-thinking"
      data-variant={variant}
      style={wrap}
    >
      <span
        aria-hidden="true"
        data-testid="milo-thinking-orb"
        style={{
          flex: '0 0 auto',
          width: orbSize, height: orbSize, borderRadius: '50%',
          background: `radial-gradient(circle at 32% 30%, ${GOLD}, ${GOLD_DK})`,
          boxShadow: `0 0 0 ${isPanel ? 4 : 3}px rgba(198,167,90,0.16)`,
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          color: DARK, fontWeight: 800, fontSize: orbSize * 0.5,
          lineHeight: 1, fontFamily: 'Georgia, "Times New Roman", serif',
          animation: animate ? 'milo-thinking-pulse 1.6s ease-in-out infinite' : 'none',
        }}
      >
        M
      </span>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
        <span
          data-testid="milo-thinking-label"
          style={{
            color: isPanel ? DARK : MUTED,
            fontSize: isPanel ? 13 : 12,
            fontWeight: isPanel ? 700 : 600,
            fontStyle: isPanel ? 'normal' : 'italic',
          }}
        >
          {label}
        </span>

        {hasSteps && (
          <span
            data-testid="milo-thinking-step"
            style={{ color: MUTED, fontSize: 11.5, fontStyle: 'italic' }}
          >
            {steps[stepIdx]}
          </span>
        )}

        {showReassure && (
          <span
            data-testid="milo-thinking-reassure"
            style={{ color: GOLD_DK, fontSize: 11, fontWeight: 600 }}
          >
            {REASSURANCE}
          </span>
        )}
      </div>

      <style>{`
        @keyframes milo-thinking-pulse {
          0%, 100% { transform: scale(1);    opacity: 0.92; }
          50%      { transform: scale(1.12); opacity: 1; }
        }
        @media (prefers-reduced-motion: reduce) {
          [data-testid="milo-thinking-orb"] { animation: none !important; }
        }
      `}</style>
    </div>
  );
}
